import { useState, useEffect, useContext, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

export default function InvitePage() {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const { t } = useLingui();
  const { ssoLogin, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [_inviteValid, setInviteValid] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [signupCode, setSignupCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const velocityLinesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hash) {
      validateInvite();
    } else {
      setError(t`Invalid invite link`);
      setLoading(false);
    }
  }, [hash]);

  // Create velocity lines animation
  useEffect(() => {
    if (!velocityLinesContainerRef.current) return;
    
    const container = velocityLinesContainerRef.current;
    // Clear any existing lines
    container.innerHTML = '';
    
    // Create 20 velocity lines
    for (let i = 0; i < 20; i++) {
      const line = document.createElement('div');
      line.className = 'velocity-line';
      line.style.left = Math.random() * 100 + 'vw';
      line.style.animationDelay = Math.random() * 5 + 's';
      line.style.animationDuration = (Math.random() * 5 + 5) + 's';
      container.appendChild(line);
    }

    return () => {
      container.innerHTML = '';
    };
  }, [showSignup]);

  const validateInvite = async () => {
    try {
      // Check if invite is valid (we'll validate on backend during signup)
      setInviteValid(true);
      setShowSignup(true);
    } catch (err: any) {
      setError(err.response?.data?.error || t`Invalid or expired invite link`);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError(t`Please fill in all fields`);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t`Passwords do not match`);
      return;
    }

    if (formData.password.length < 8) {
      setError(t`Password must be at least 8 characters`);
      return;
    }
    if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setError(t`Password must include an uppercase letter, a lowercase letter, and a number`);
      return;
    }

    // Verify-before-create: send the 6-digit code; the account (and invite
    // consumption) only happen after the code is proven.
    try {
      await axios.post(`${API_BASE_URL}/api/user/signup/start`, { email: formData.email });
      setSignupCode('');
      setCodeError('');
      setResendCooldown(60);
      setVerificationSent(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || t`Could not send a verification code`);
    }
  };

  const handleVerifyAndRegister = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(signupCode.trim())) {
      setCodeError(t`Enter the 6-digit code from the email.`);
      return;
    }
    setVerifyBusy(true);
    setCodeError('');
    try {
      const verify = await axios.post(`${API_BASE_URL}/api/user/signup/verify`, {
        email: formData.email,
        code: signupCode.trim()
      });
      if (!verify.data?.verified || !verify.data?.signupToken) {
        setCodeError(verify.data?.error || t`That code is invalid or has expired.`);
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/api/user/register`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        inviteHash: hash,
        signupToken: verify.data.signupToken
      });

      if (response.data?.token) {
        const token = response.data.token;
        const userData = response.data.user || {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName
        };

        localStorage.setItem('privacypal_token', token);
        localStorage.setItem('privacypal_user', JSON.stringify(userData));
        axios.defaults.headers.common['x-access-token'] = token;

        await refreshUser();
        navigate('/signup');
      } else {
        setCodeError(response.data?.error || t`Registration failed`);
      }
    } catch (err: any) {
      setCodeError(err.response?.data?.error || err.message || t`Verification failed`);
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleResendInviteVerification = async () => {
    setResendBusy(true);
    setResendMessage('');
    try {
      await axios.post(`${API_BASE_URL}/api/user/signup/start`, { email: formData.email });
      setResendMessage(t`If that address can receive mail, we've sent a new code.`);
      setResendCooldown(60);
    } catch {
      setResendMessage(t`If that address can receive mail, we've sent a new code.`);
    } finally {
      setResendBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#050a0f', 
        color: '#e6f1ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner mb-4" style={{ 
            borderColor: 'rgba(51, 102, 153, 0.3)',
            borderTopColor: '#336699'
          }}></div>
          <p style={{ color: '#99ccff' }}><Trans>Validating invite link...</Trans></p>
        </div>
      </div>
    );
  }

  if (error && !showSignup) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#050a0f', 
        color: '#e6f1ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          maxWidth: '28rem',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <svg style={{ width: '3rem', height: '3rem', color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.5rem' }}><Trans>Invalid Invite Link</Trans></h1>
          <p style={{ color: '#99ccff', marginBottom: '1.5rem' }}>{error}</p>
          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              background: '#336699',
              color: 'white',
              padding: '0.625rem 1.5rem',
              borderRadius: '0.5rem',
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#4a86c2'}
            onMouseOut={(e) => e.currentTarget.style.background = '#336699'}
          >
            <Trans>Go to Login</Trans>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --cobalt: #336699;
          --cobalt-dark: #1a3a5a;
          --cobalt-light: #4a86c2;
          --glass: rgba(255, 255, 255, 0.03);
          --glass-border: rgba(255, 255, 255, 0.1);
        }

        body {
          background-color: #050a0f;
          color: #e6f1ff;
          font-family: 'Inter', sans-serif;
          overflow-x: hidden;
          margin: 0;
        }

        .mono { font-family: 'JetBrains Mono', monospace; }

        /* Vitreous Velocity Background */
        .bg-mesh {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          background: 
            radial-gradient(circle at 20% 30%, rgba(51, 102, 153, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(51, 102, 153, 0.1) 0%, transparent 50%);
        }

        .velocity-line {
          position: absolute;
          width: 1px;
          height: 100px;
          background: linear-gradient(to bottom, transparent, var(--cobalt), transparent);
          opacity: 0.3;
          animation: moveDown 8s linear infinite;
        }

        @keyframes moveDown {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(100vh); }
        }

        /* Glass Cards */
        .vitreous-card {
          background: var(--glass);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid var(--glass-border);
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .vitreous-card:hover {
          border-color: var(--cobalt);
          background: rgba(51, 102, 153, 0.05);
        }

        /* Typography & UI */
        .hero-text {
          font-size: clamp(3rem, 8vw, 6rem);
          line-height: 0.9;
          letter-spacing: -0.04em;
          background: linear-gradient(180deg, #fff 0%, rgba(255,255,255,0.6) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          padding-bottom: 0.15em;
          overflow: visible;
        }

        .sso-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid var(--glass-border);
          background: rgba(255,255,255,0.05);
          transition: all 0.2s ease;
          font-weight: 500;
          color: #e6f1ff;
          cursor: pointer;
          width: 100%;
        }

        .sso-button:hover {
          background: rgba(255,255,255,0.1);
          transform: translateY(-1px);
        }

        .input-field {
          background: rgba(0,0,0,0.3);
          border: 1px solid var(--glass-border);
          padding: 14px 16px;
          border-radius: 8px;
          width: 100%;
          outline: none;
          transition: border-color 0.3s ease;
          color: #e6f1ff;
          font-family: 'Inter', sans-serif;
        }

        .input-field::placeholder {
          color: rgba(230, 241, 255, 0.4);
        }

        .input-field:focus {
          border-color: var(--cobalt);
        }

        .primary-btn {
          background: var(--cobalt);
          color: white;
          padding: 14px;
          border-radius: 8px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          transition: all 0.3s ease;
          box-shadow: 0 0 20px rgba(51, 102, 153, 0.4);
          border: none;
          cursor: pointer;
          width: 100%;
        }

        .primary-btn:hover {
          background: var(--cobalt-light);
          transform: scale(1.02);
          box-shadow: 0 0 30px rgba(51, 102, 153, 0.6);
        }

        .badge {
          background: rgba(51, 102, 153, 0.2);
          border: 1px solid var(--cobalt);
          color: #99ccff;
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .reveal {
          animation: revealUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
          transform: translateY(20px);
        }

        @keyframes revealUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .latency-pulse {
          width: 8px;
          height: 8px;
          background: #00ffaa;
          border-radius: 50%;
          display: inline-block;
          box-shadow: 0 0 10px #00ffaa;
          margin-right: 8px;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ff6b6b;
          padding: 12px;
          border-radius: 8px;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }
      `}</style>

      <div style={{ 
        backgroundColor: '#050a0f', 
        color: '#e6f1ff', 
        fontFamily: "'Inter', sans-serif",
        overflowX: 'hidden',
        margin: 0,
        minHeight: '100vh'
      }}>
        <div className="bg-mesh"></div>
        <div ref={velocityLinesContainerRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1, pointerEvents: 'none' }}></div>

        {/* Navigation */}
        <nav style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '1.5rem 2rem',
          maxWidth: '80rem',
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img 
              src="/privacypal-logo-light.png" 
              alt="PrivacyPal" 
              style={{ height: '72px', maxHeight: '72px', width: 'auto', display: 'block' }}
            />
          </div>
          <div className="badge"><Trans>Invite Only</Trans></div>
        </nav>

        <main style={{ 
          maxWidth: '80rem', 
          margin: '0 auto', 
          padding: '3rem 2rem 6rem', 
          position: 'relative', 
          zIndex: 10,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '4rem',
          alignItems: 'center'
        }}>
          
          {/* Left Column: Value Prop */}
          <div className="reveal" style={{ animationDelay: '0.1s', paddingBottom: '60px', overflow: 'visible' }}>
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className="mono" style={{ fontSize: '0.75rem', color: '#336699', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase' }}><Trans>Early Beta Release</Trans></span>
            </div>
            <h1 className="hero-text" style={{ fontWeight: 800, marginBottom: '1rem', overflow: 'visible', paddingBottom: '0.2em' }}>
              <Trans>Make AI Safe for Work.<br/>Instantly.</Trans>
            </h1>
            <p style={{ fontSize: '1.25rem', color: 'rgba(147, 197, 253, 0.6)', maxWidth: '32rem', marginBottom: '3rem', lineHeight: '1.75' }}>
              <Trans>The first-ever Privacy Agent designed specifically for the Generative AI era. Stop the manual redaction. Stop the data leaks. Get 100% accurate results without the risk.</Trans>
            </p>

            {/* Features Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', marginBottom: '3rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span className="mono" style={{ color: '#336699', fontWeight: 700, fontSize: '0.875rem' }}><Trans>LATENCY</Trans></span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="latency-pulse"></span>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>&lt;1.0ms</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)' }}><Trans>Real-time scrubbing</Trans></p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span className="mono" style={{ color: '#336699', fontWeight: 700, fontSize: '0.875rem' }}><Trans>ACCURACY</Trans></span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <svg style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem', color: '#60a5fa' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                    <path d="M12 2v4m0 12v4M2 12h4m12 0h4" strokeWidth="2"/>
                  </svg>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>100%</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)' }}><Trans>Context-aware masking</Trans></p>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1.5rem', 
              padding: '1rem',
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '1rem'
            }}>
              <div style={{ display: 'flex', gap: '-0.75rem' }}>
                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', border: '2px solid #050a0f', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, marginLeft: '-0.75rem' }}>GPT</div>
                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', border: '2px solid #050a0f', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, marginLeft: '-0.75rem' }}>CLD</div>
                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', border: '2px solid #050a0f', background: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, marginLeft: '-0.75rem' }}>GEM</div>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}><Trans>Installs in your browser. Works across major LLMs instantly.</Trans></p>
            </div>
          </div>

          {/* Right Column: Signup Form */}
          <div className="reveal" style={{ animationDelay: '0.3s' }}>
            <div className="vitreous-card" style={{ 
              borderRadius: '32px', 
              padding: '2.5rem', 
              maxWidth: '28rem', 
              margin: '0 auto', 
              position: 'relative', 
              overflow: 'hidden'
            }}>
              {/* Decorative Glow */}
              <div style={{ 
                position: 'absolute', 
                top: '-6rem', 
                right: '-6rem', 
                width: '12rem', 
                height: '12rem', 
                background: '#336699', 
                opacity: 0.2, 
                filter: 'blur(80px)'
              }}></div>
              
              {verificationSent ? (
                <>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}><Trans>Verify your email</Trans></h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1.5rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                    <Trans>
                      If <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{formData.email}</strong> can receive mail, we've sent it a 6-digit code. Enter it below to verify your address and finish accepting your invite — your account is only created after this step.
                    </Trans>
                  </p>
                  {codeError && <div className="error-message">{codeError}</div>}
                  <form onSubmit={handleVerifyAndRegister}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="\d{6}"
                      maxLength={6}
                      placeholder="000000"
                      value={signupCode}
                      onChange={(e) => { setSignupCode(e.target.value.replace(/\D/g, '')); setCodeError(''); }}
                      className="input-field"
                      style={{ textAlign: 'center', fontSize: '1.75rem', letterSpacing: '0.5em', marginBottom: '1rem', fontFamily: "'JetBrains Mono', monospace" }}
                      autoFocus
                    />
                    <button type="submit" className="primary-btn" disabled={verifyBusy || signupCode.length !== 6} style={{ marginBottom: '0.75rem', opacity: verifyBusy || signupCode.length !== 6 ? 0.6 : 1 }}>
                      {verifyBusy ? t`Verifying…` : t`Verify & accept invite`}
                    </button>
                  </form>
                  <button
                    type="button"
                    onClick={handleResendInviteVerification}
                    disabled={resendBusy || resendCooldown > 0}
                    className="primary-btn"
                    style={{ background: 'rgba(255,255,255,0.08)', marginBottom: '0.75rem', opacity: resendBusy || resendCooldown > 0 ? 0.6 : 1 }}
                  >
                    {resendBusy ? t`Sending…` : resendCooldown > 0 ? t`Resend code (${resendCooldown}s)` : t`Resend code`}
                  </button>
                  {resendMessage && (
                    <p style={{ fontSize: '0.8rem', textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginBottom: '0.75rem' }}>{resendMessage}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => { setVerificationSent(false); setResendMessage(''); setCodeError(''); }}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', cursor: 'pointer', width: '100%', padding: '0.5rem' }}
                  >
                    <Trans>← Use a different email</Trans>
                  </button>
                </>
              ) : (
              <>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}><Trans>Claim Your Invite</Trans></h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginBottom: '2rem', fontSize: '0.875rem' }}><Trans>Enter your details to activate your private instance of PrivacyPal.</Trans></p>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                <button 
                  type="button"
                  className="sso-button"
                  onClick={() => ssoLogin('google', hash || undefined)}
                >
                  <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.67l-3.57-2.77c-1 .67-2.27 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z"/>
                  </svg>
                  <Trans>Sign up with Google</Trans>
                </button>
                <button 
                  type="button"
                  className="sso-button"
                  onClick={() => ssoLogin('microsoft', hash || undefined)}
                >
                  <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 23 23">
                    <path fill="#f3f3f3" d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z"/>
                  </svg>
                  <Trans>Sign up with Microsoft</Trans>
                </button>
              </div>

              <div style={{ position: 'relative', marginBottom: '2rem' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}></div>
                </div>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  <span style={{ background: '#0c141d', padding: '0 0.5rem', color: 'rgba(255, 255, 255, 0.3)', letterSpacing: '0.1em' }}><Trans>Or via Email</Trans></span>
                </div>
              </div>

              <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Full Name</Trans></label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <input
                      type="text"
                      placeholder={t`First`}
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="input-field"
                      required
                    />
                    <input
                      type="text"
                      placeholder={t`Last`}
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Work Email</Trans></label>
                  <input
                    type="email"
                    placeholder={t`name@company.com`}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Password</Trans></label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-field"
                    required
                    minLength={8}
                  />
                  <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.4)', marginTop: '0.5rem' }}>
                    <Trans>At least 8 characters, including an uppercase letter, a lowercase letter, and a number.</Trans>
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Confirm Password</Trans></label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <button type="submit" className="primary-btn" style={{ marginTop: '1rem' }}>
                  <Trans>Initialize Privacy Agent</Trans>
                </button>
              </form>

              <p style={{ fontSize: '10px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.3)', marginTop: '1.5rem', lineHeight: '1.75' }}>
                <Trans>
                  By clicking initialize, you agree to our <a href="https://privacypal.ai/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>. <br />
                  PrivacyPal does not have access to your LLM prompt history.
                </Trans>
              </p>
              </>
              )}
            </div>
          </div>
        </main>

        {/* Bottom Stat Bar */}
        <footer style={{ 
          position: 'fixed', 
          bottom: 0, 
          width: '100%', 
          background: 'rgba(5, 10, 15, 0.8)', 
          backdropFilter: 'blur(24px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          padding: '1rem 0',
          zIndex: 20
        }}>
          <div style={{ 
            maxWidth: '80rem', 
            margin: '0 auto', 
            padding: '0 2rem', 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '1rem'
          }}>
            <div style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255, 255, 255, 0.2)', textTransform: 'uppercase', letterSpacing: '0.2em' }}>
              <Trans>&copy; 2026 WEB3 REX INC. All Rights Reserved.</Trans>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

